<script setup lang="ts">
import type { Variants } from '@/common';
import type { Icons } from '@/components/misc/Icon.vue';

const props = withDefaults(
    defineProps<{
        variant?: Variants;
        disabled?: boolean;
        loading?: boolean;
        label?: string;
        outline?: boolean;
        form?: boolean;
        noWrap?: boolean;
        class?: any;
        icon?: Icons | Component | FunctionalComponent;
        iconAfter?: boolean;
        defaultClasses?: any;
        help?: any;
    }>(),
    {
        variant: 'primary',
        noWrap: true,
        defaultClasses: 'align-self-end-column d-flex align-items-center flex-nowrap gap-1',
    },
);

const { disabled } = useDisabled({
    combine: toRef(props, 'disabled'),
});

const hidden = inject<Ref<boolean> | undefined>('__hide_form_btn', undefined);

const bind = computed(() => {
    const bind: Record<string, any> = {
        class: [],
    };
    if (props.outline) {
        bind['class'].push(`btn-outline-${props.variant ?? 'primary'}`);
    } else if (props.variant != null) {
        bind['class'].push(`btn-${props.variant}`);
    }
    return bind;
});
</script>

<template>
    <button
        v-if="!form || hidden == null || hidden === false"
        v-bind="bind"
        class="btn"
        :disabled="disabled || loading"
        :class="[props.defaultClasses, props.class]"
        :style="
            noWrap || loading
                ? {
                      whiteSpace: 'nowrap',
                  }
                : undefined
        "
    >
        <Spinner v-if="loading" />
        <template v-else-if="!iconAfter">
            <Help v-if="typeof help === 'string'" :content="help" />
            <Icon v-else-if="typeof icon === 'string'" :icon="icon" class="icon-sm" />
            <component v-else-if="icon" :is="icon"></component>
        </template>
        <slot>
            <div v-if="label">
                {{ label }}
            </div>
        </slot>
        <template v-if="!loading && iconAfter">
            <Help v-if="typeof help === 'string'" :content="help" />
            <Icon v-else-if="typeof icon === 'string'" :icon="icon" class="icon-sm" />
            <component v-else-if="icon" :is="icon"></component>
        </template>
    </button>
</template>

<style scoped>
.flex-column:not(.align-items-start):not(.align-items-center) > .align-self-end-column,
.flex-column:not(.align-items-start):not(.align-items-center)
    > *:not(.align-items-center):not(.align-items-star)
    > .align-self-end-column {
    align-self: flex-end;
}
</style>

<style>
.hide-buttons .btn {
    display: none !important;
}
</style>
